import { motion } from "framer-motion";
import { Linkedin, Dribbble } from "lucide-react";
import { useEffect, useState } from "react";

function XIcon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
    </svg>
  );
}

export default function FooterCTA() {
  const [time, setTime] = useState("");

  useEffect(() => {
    const update = () => {
      try {
        const now = new Date();
        const fmt = new Intl.DateTimeFormat("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
          timeZone: "Africa/Lagos",
        }).format(now);
        setTime(`${fmt}pm`);
      } catch {
        setTime("18:49pm");
      }
    };
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="contact"
      data-testid="footer-cta-section"
      className="relative bg-zinc-950 text-white py-24 sm:py-32 grain"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
        {/* Socials */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center gap-3 mb-10"
        >
          <SocialButton href="https://www.linkedin.com/in/obiajuluanayo/" testid="social-linkedin">
            <Linkedin className="h-4 w-4" />
          </SocialButton>
          <SocialButton href="https://x.com/ObiajuluAnayo" testid="social-twitter">
            <XIcon className="h-3.5 w-3.5" />
          </SocialButton>
          <SocialButton href="https://dribbble.com/anayo_designs" testid="social-dribbble">
            <Dribbble className="h-4 w-4" />
          </SocialButton>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          data-testid="footer-heading"
          className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.05]"
        >
          Let&apos;s build something{" "}
          <span className="italic font-light text-zinc-200">impactful</span>{" "}
          <span className="brand-gradient-text">together.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-6 text-zinc-400 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed"
        >
          Whether you&apos;re looking to collaborate, invite me to speak, or
          discuss your next product idea — I&apos;d love to hear from you.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10"
        >
          <a
            href="mailto:obiianayo@gmail.com"
            data-testid="footer-lets-talk-btn"
            className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-brand-500 hover:text-white transition-colors text-sm font-medium px-6 py-3 rounded-full"
          >
            Let&apos;s talk
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-500"></span>
          </a>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 mt-20 sm:mt-28 pt-6 border-t border-white/10 flex items-center justify-between flex-wrap gap-3 text-xs text-zinc-500">
        <span data-testid="footer-copyright">@2025 OBI ANAYO</span>
        <span data-testid="footer-location">
          Lagos, Nigeria · <span className="text-zinc-400">{time}</span>
        </span>
      </div>
    </section>
  );
}

function SocialButton({ href, children, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-zinc-300 hover:text-white hover:border-brand-400/60 hover:bg-brand-500/10 transition-colors"
    >
      {children}
    </a>
  );
}
